import { zodResolver } from '@hookform/resolvers/zod';
import {
	MenuItem,
	Select,
	type SelectChangeEvent,
	TextField,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { NumberInput } from 'components/number-input/number-input';
import dayjs from 'dayjs';
import {
	selectSelectedEvents,
	setIsEventsFormDirty,
} from 'features/events/redux/events-form-slice';
import { useUser } from 'features/users/context/userContext';
import {
	EventCategoryEnum,
	type EventFormData,
	type EventRecordInput,
} from 'graphql/gql-types';
import { labels } from 'locales/en.label';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
	MdDeleteOutline,
	MdOutlineAddBox,
	MdOutlineIndeterminateCheckBox,
	MdOutlineSave,
} from 'react-icons/md';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { z } from 'zod';
import './EventsForm.scss';
import { CustomTooltip } from '@/components/custom-tooltip';
import { Button } from '@/components/ui/button';

const eventTypeOptions = [
	{ label: 'General', value: EventCategoryEnum.general },
	{ label: 'Holiday', value: EventCategoryEnum.holiday },
	{ label: 'Political', value: EventCategoryEnum.political },
	{ label: 'Religious', value: EventCategoryEnum.religious },
	{ label: 'School', value: EventCategoryEnum.school },
	{ label: 'Sports', value: EventCategoryEnum.sports },
	{ label: 'Festival', value: EventCategoryEnum.festival },
];

export interface EventsFormRef {
	values: EventFormData;
	dirty: boolean;
	resetForm: (params: { values: EventFormData }) => void;
}

const MIN_AMOUNT = -100;
const MAX_AMOUNT = 100;

// Create mutable copies of form data to avoid read-only array errors
const createMutableFormData = (data: EventFormData): EventFormData => ({
	...data,
	records: data.records
		? data.records.map((record) => ({
				...record,
				// Ensure date fields are also mutable by creating new references
				start_date: record?.start_date ? String(record.start_date) : '',
				end_date: record?.end_date ? String(record.end_date) : '',
			}))
		: [],
});

const eventValidationSchema = z.object({
	name: z.string().min(1, labels.events.validation.required),

	category: z.nativeEnum(EventCategoryEnum, {
		errorMap: () => ({ message: labels.events.validation.required }),
	}),

	records: z
		.array(
			z
				.object({
					id: z.string().optional(),
					event_id: z.string().optional().or(z.literal('')),
					start_date: z.preprocess(
						(val) => {
							if (typeof val === 'string' || typeof val === 'number') {
								const date = new Date(val);
								return Number.isNaN(date.getTime()) ? undefined : date;
							}
							return undefined;
						},
						z.date({ invalid_type_error: labels.events.validation.required }),
					),

					end_date: z.preprocess(
						(val) => {
							if (typeof val === 'string' || typeof val === 'number') {
								const date = new Date(val);
								return Number.isNaN(date.getTime()) ? undefined : date;
							}
							return undefined;
						},
						z.date({ invalid_type_error: labels.events.validation.required }),
					),
				})
				.refine((data) => data.end_date >= data.start_date, {
					message: labels.events.validation.end_date_err,
					path: ['end_date'],
				}),
		)
		.min(1, { message: labels.events.validation.required }),

	occ_impact: z
		.union([z.string(), z.number(), z.undefined()])
		.transform((val) => {
			if (val === undefined || val === '') {
				return undefined;
			}
			const num = Number(val);
			return Number.isNaN(num) ? undefined : num;
		})
		.refine(
			(num) => num === undefined || (num >= MIN_AMOUNT && num <= MAX_AMOUNT),
			{
				message: `(Enter value between ${MIN_AMOUNT} to ${MAX_AMOUNT})`,
			},
		),
});

export interface EventFormProps {
	id: string;
	formRef: React.Ref<EventsFormRef> | null;
	onFormReset: () => void;
	readonly: boolean;
	formData: EventFormData;
	onSaveClick: () => void;
	onDeleteClick: () => void;
	onRecordDelete: (record: EventRecordInput) => void;
	setIsBlocked: (isBlocked: boolean) => void;
}

const EventsForm = forwardRef<any, EventFormProps>((props, _unusedRef) => {
	const {
		formRef,
		formData,
		onSaveClick,
		onDeleteClick,
		onRecordDelete,
		readonly,
		setIsBlocked,
	} = props;

	const dispatch = useAppDispatch();
	const { isReadOnly } = useUser();
	const selectedEvents = useAppSelector(selectSelectedEvents);

	const {
		control,
		register,
		handleSubmit,
		reset,
		getValues,
		watch,
		setValue,
		formState: { errors, isDirty },
	} = useForm<EventFormData>({
		resolver: zodResolver(eventValidationSchema),
		defaultValues: createMutableFormData(formData),
		mode: 'onChange',
	});

	useEffect(() => {
		reset(createMutableFormData(formData));
	}, [formData, reset]);

	const {
		fields: recordFields,
		append,
		remove,
	} = useFieldArray({
		control,
		name: 'records',
	});

	const occImpactVal = watch('occ_impact');
	const [tempInputValue, setTempInputValue] = useState(
		(occImpactVal ? occImpactVal : '').toString(),
	);
	useEffect(() => {
		dispatch(setIsEventsFormDirty(isDirty));
		setIsBlocked(isDirty);
	}, [isDirty, dispatch, setIsBlocked]);

	useEffect(() => {
		setTempInputValue((occImpactVal ?? '').toString());
	}, [occImpactVal]);

	useImperativeHandle(formRef, () => ({
		get values() {
			return getValues();
		},
		resetForm: ({ values }: { values: EventFormData }) => {
			reset(createMutableFormData(values));
		},
		get dirty() {
			return isDirty;
		},
	}));

	const submitHandler = () => {
		onSaveClick();
		// Clear the blocked state after save
		setIsBlocked(false);
	};

	const onCategoryChange = (event: SelectChangeEvent) => {
		const { value } = event.target;
		setValue('category', value as EventCategoryEnum, { shouldDirty: true });
	};

	return (
		<div className='events'>
			<form
				className='events__form'
				id='events-form'
				onSubmit={handleSubmit(submitHandler)}
			>
				{/* Event Name */}
				<div className='events__form__row'>
					<div className='events__form__row-item events__form__row-item-event-name'>
						<TextField
							id='name'
							variant='outlined'
							placeholder='Event Name'
							disabled={isReadOnly || readonly}
							{...register('name')}
							error={!!errors.name}
							helperText={errors.name?.message}
						/>
					</div>
				</div>

				{/* Event Category */}
				<div className='events__form__row'>
					<div className='events__form__row-item events__form__row-item-event-type'>
						<Select
							defaultValue=''
							inputProps={{
								name: 'category',
								id: 'category',
							}}
							disabled={isReadOnly || readonly}
							value={watch('category') || ''}
							onChange={onCategoryChange}
							error={!!errors.category}
						>
							{eventTypeOptions.map(({ label, value }) => (
								<MenuItem key={label} value={value}>
									{label}
								</MenuItem>
							))}
						</Select>
						{errors.category && (
							<p className='error-text'>{errors.category.message}</p>
						)}
					</div>
				</div>

				{/* Records Array */}
				<div className='events__form__row events__form__row-event-records'>
					<div className='events__form__row-event-record'>
						{recordFields &&
							[...recordFields].map((record, index) => {
								return (
									<div
										className='events__form__row events__form__row-event-record-date'
										key={record.id || index}
									>
										<div className='events__form__row-item'>
											{/* Start Date */}

											<DatePicker
												label={labels.events.start_date}
												value={
													watch(`records.${index}.start_date`)
														? dayjs(watch(`records.${index}.start_date`))
														: null
												}
												onChange={(date) => {
													if (date) {
														setValue(
															`records.${index}.start_date`,
															date.format('YYYY-MM-DD'),
															{
																shouldDirty: true,
															},
														);
													}
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														margin='normal'
														error={!!errors.records?.[index]?.start_date}
														helperText={
															errors.records?.[index]?.start_date?.message
														}
													/>
												)}
											/>
										</div>

										<div className='events__form__row-item'>
											{/* End Date */}

											<DatePicker
												label={labels.events.end_date}
												value={
													watch(`records.${index}.end_date`)
														? dayjs(watch(`records.${index}.end_date`))
														: null
												}
												onChange={(date) => {
													if (date) {
														setValue(
															`records.${index}.end_date`,
															date.format('YYYY-MM-DD'),
															{
																shouldDirty: true,
															},
														);
													}
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														margin='normal'
														error={!!errors.records?.[index]?.end_date}
														helperText={
															errors.records?.[index]?.end_date?.message
														}
													/>
												)}
											/>
										</div>

										{/* Remove Record Button */}
										<div className='events__form__row-item'>
											<CustomTooltip
												content={
													<p>{labels.events.tooltip.remove_event_record}</p>
												}
											>
												<span>
													<IconButton
														disableFocusRipple
														disableRipple
														aria-label='remove-event-record'
														onClick={() => {
															if (recordFields.length > 1) {
																remove(index);
																onRecordDelete(record as EventRecordInput);
															}
														}}
														className='events__form-remove-date-btn'
														disabled={recordFields.length === 1}
													>
														<MdOutlineIndeterminateCheckBox size={22} />
													</IconButton>
												</span>
											</CustomTooltip>
										</div>
									</div>
								);
							})}
					</div>

					{/* Add Record Button */}
					<div className='events__form__row-item-add'>
						<CustomTooltip
							content={<p>{labels.events.tooltip.add_event_record}</p>}
						>
							<span>
								<IconButton
									disableFocusRipple
									disableRipple
									aria-label='add-event-record'
									onClick={() =>
										append({
											id: '',
											start_date: '',
											end_date: '',
											event_id: watch('event_id') || '',
										})
									}
									className='events__form-add-date-btn'
								>
									<MdOutlineAddBox size={22} />
								</IconButton>
							</span>
						</CustomTooltip>
					</div>
				</div>

				{/* Occupancy Impact */}
				<div className='events__form__row'>
					<div className='events__form__row-item events__form__row-item-label label'>
						{labels.events.occupancy_impact}
						<div className='events__form__row-item-label-sm'>
							{`(Enter value between ${MIN_AMOUNT} to ${MAX_AMOUNT})`}
						</div>
					</div>
					<div className='events__form__row-item'>
						<div className='magnitude'>
							<NumberInput
								name='occ_impact'
								disabled={isReadOnly || readonly}
								showCurrencySymbol={false}
								allowSigns={true}
								maxAmount={MAX_AMOUNT}
								minAmount={MIN_AMOUNT}
								isFloat={false}
								value={tempInputValue}
								onChange={(e) => {
									setTempInputValue(e.target.value); // store typed string
								}}
								onBlur={(e) => {
									let parsed = Number(e.target.value);
									if (Number.isNaN(parsed)) parsed = 0;
									setValue('occ_impact', parsed, { shouldDirty: true });
									setTempInputValue(parsed.toString());
								}}
							/>
						</div>
						{errors.occ_impact && (
							<p className='error-text'>{errors.occ_impact.message}</p>
						)}
					</div>
					<div className='events__form__row-item-percentile label'>{'%'}</div>
				</div>

				{/* Footer (Save/Delete) */}
				<div className='events__form__row events__form-footer'>
					<Button
						type='submit'
						disabled={!isDirty}
						className='events__form-save-btn'
					>
						<MdOutlineSave size={22} />
						{labels.common.save}
					</Button>
					<Button
						type='button'
						onClick={onDeleteClick}
						className='events__form-delete-btn'
						disabled={selectedEvents.length === 0}
					>
						<MdDeleteOutline size={22} /> {labels.common.delete}
					</Button>
				</div>
			</form>
		</div>
	);
});

EventsForm.displayName = 'EventsForm';
export default EventsForm;
